import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Card, CardContent, Chip, CircularProgress, Divider, Grid2, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AppPage from "../components/layout/AppPage";
import AppPageHeader from "../components/layout/AppPageHeader";
import { AppDispatch } from "../reduxStore/Store";
import {
  fetchVentesJour,
  fetchEncaissementsJour,
  fetchCommandesEnCours,
  fetchFacturesMois,
  fetchNouveauxClients,
  fetchArticlesActifs,
  clearDashboardData,
} from "../reduxStore/DashboardSlice";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatMontant = (val: number) =>
  new Intl.NumberFormat("fr-DZ", { maximumFractionDigits: 0 }).format(val) ;

const pctTrend = (current: number, previous: number): { label: string; up: boolean } | null => {
  if (!previous) return null;
  const pct = ((current - previous) / previous) * 100;
  return { label: (pct >= 0 ? "+" : "") + pct.toFixed(1) + "%", up: pct >= 0 };
};

const deltaTrend = (current: number, previous: number): { label: string; up: boolean } | null => {
  if (!previous) return null;
  const d = current - previous;
  return { label: (d >= 0 ? "+" : "") + d, up: d >= 0 };
};

// ── StatCard ──────────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  trend?: { label: string; up: boolean } | null;
  trendLabel?: string;
  color: string;
  bgColor: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, trend, trendLabel = "vs hier", color, bgColor, loading }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "16px",
      bgcolor: "#ffffff",
      border: "1px solid #e4e7ec",
      transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
      "&:hover": { transform: "translateY(-4px)", borderColor: "#c7d2fe", boxShadow: "0 12px 32px rgba(16,24,40,0.10)" },
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box flex={1}>
          <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>
            {title}
          </Typography>
          {loading ? (
            <CircularProgress size={24} sx={{ my: 0.5 }} />
          ) : (
            <Typography variant="h4" fontWeight={700} color="text.primary" lineHeight={1.2}>
              {value}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 52, height: 52, borderRadius: 2.5,
            background: bgColor, display: "flex", alignItems: "center",
            justifyContent: "center", color, flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      {trend && (
        <>
          <Divider sx={{ my: 1.5, opacity: 0.4 }} />
          <Box display="flex" alignItems="center" gap={0.5}>
            <Chip
              label={trend.label}
              size="small"
              sx={{
                bgcolor: trend.up ? "#e8f5e9" : "#fce4ec",
                color: trend.up ? "#2e7d32" : "#c62828",
                fontWeight: 600, fontSize: "0.7rem", height: 22,
              }}
            />
            <Typography variant="caption" color="text.secondary">{trendLabel}</Typography>
          </Box>
        </>
      )}
    </CardContent>
  </Card>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    ventesJour, encaissementsJour, commandesEnCours,
    facturesMois, nouveauxClients, articlesActifs, loading,
  } = useSelector((state: any) => state.DashboardSlice);

  const deptId = useSelector((state: any) => state.AppSlice.selectedDepartement?.id ?? null);

  useEffect(() => {
    const params = deptId ? { departement_id: deptId } : {};
    dispatch(fetchVentesJour(params));
    dispatch(fetchEncaissementsJour(params));
    dispatch(fetchCommandesEnCours(params));
    dispatch(fetchFacturesMois(params));
    dispatch(fetchNouveauxClients());
    dispatch(fetchArticlesActifs(params));
    return () => { dispatch(clearDashboardData()); };
  }, [dispatch, deptId]);

  const stats: StatCardProps[] = [
    {
      title:    "Ventes du jour",
      value:    ventesJour ? formatMontant(ventesJour.montant_total) : "—",
      subtitle: ventesJour ? `${ventesJour.nb_documents} transaction(s) aujourd'hui` : "Chargement…",
      icon:     <TrendingUpIcon fontSize="medium" />,
      trend:    ventesJour ? pctTrend(ventesJour.montant_total, ventesJour.montant_precedent) : null,
      color: "#1976d2", bgColor: "#e3f2fd",
    },
    {
      title:    "Encaissements",
      value:    encaissementsJour ? formatMontant(encaissementsJour.montant_total) : "—",
      subtitle: encaissementsJour ? `${encaissementsJour.nb_reglements} règlement(s) reçu(s) ce jour` : "Chargement…",
      icon:     <AccountBalanceWalletIcon fontSize="medium" />,
      trend:    encaissementsJour ? pctTrend(encaissementsJour.montant_total, encaissementsJour.montant_precedent) : null,
      color: "#2e7d32", bgColor: "#e8f5e9",
    },
    {
      title:    "Commandes en cours",
      value:    commandesEnCours ? String(commandesEnCours.nb_documents) : "—",
      subtitle: "En attente de traitement",
      icon:     <ShoppingCartIcon fontSize="medium" />,
      trend:    null,
      color: "#ed6c02", bgColor: "#fff3e0",
    },
    {
      title:    "Nouveaux clients",
      value:    nouveauxClients ? String(nouveauxClients.nb_clients) : "—",
      subtitle: "Enregistrés ce mois",
      icon:     <PeopleIcon fontSize="medium" />,
      trend:       nouveauxClients ? deltaTrend(nouveauxClients.nb_clients, nouveauxClients.nb_clients_precedent) : null,
      trendLabel:  "vs mois dernier",
      color: "#7b1fa2", bgColor: "#f3e5f5",
    },
    {
      title:    "Articles en stock",
      value:    articlesActifs ? String(articlesActifs.nb_articles) : "—",
      subtitle: articlesActifs ? `${articlesActifs.nb_mouvements_jour} référence(s) mouvementée(s) aujourd'hui` : "Chargement…",
      icon:     <InventoryIcon fontSize="medium" />,
      trend:    articlesActifs ? deltaTrend(articlesActifs.nb_mouvements_jour, articlesActifs.nb_mouvements_precedent) : null,
      color: "#0288d1", bgColor: "#e1f5fe",
    },
    {
      title:    "Factures du mois",
      value:    facturesMois ? String(facturesMois.nb_documents) : "—",
      subtitle: facturesMois ? `${facturesMois.nb_documents} document(s) ce mois-ci` : "Chargement…",
      icon:     <ReceiptLongIcon fontSize="medium" />,
      trend:      facturesMois ? deltaTrend(facturesMois.nb_documents, facturesMois.nb_documents_precedent) : null,
      trendLabel: "vs mois dernier",
      color: "#c62828", bgColor: "#fce4ec",
    },
  ];

  return (
    <AppPage>
      <AppPageHeader
        title="Tableau de bord"
        subtitle="Aperçu de l'activité en temps réel"
        icon={<DashboardIcon />}
        sx={{ mb: 4 }}
      />
      <Grid2 container spacing={3}>
        {stats.map((stat) => (
          <Grid2 key={stat.title} size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard {...stat} loading={loading && !ventesJour} />
          </Grid2>
        ))}
      </Grid2>
    </AppPage>
  );
};

export default Dashboard;
